import { Attachments } from '@/components/creative/ChatBox';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, MessageSquare, CheckCircle2, PenLine, RotateCcw, Clock, Play, Image as ImageIcon, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { postPlaceLabel,
  fetchPortalApproval, portalApprove, portalRequestAdjustment, downloadAssetsZip, type MediaAsset, type PortalApproval,
  fetchAssetReviews, createReview, type PortalFileState,
} from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';
import ReviewModal from '@/components/creative/ReviewModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import PromptDialog from '@/components/ui/PromptDialog';
import RichView from '@/components/ui/RichView';

interface Asset {
  id: string;
  kind: string;
  filename: string;
  content_type?: string | null;
  download_url?: string | null;
  attachment_url?: string | null;
  thumbnail_url?: string | null;
}

interface CreativeDetail {
  id: string;
  code: string;
  title: string;
  description?: string | null;
  description_html?: string | null;
  creative_type: string;
  state: string;
  posting_date?: string | null;
  posted_url?: string | null;
  posted_links?: { platform: string; url: string }[];
}

interface CommentRow {
  attachments?: Array<{ path: string; name: string; content_type: string; size: number; url?: string | null }>;
  id: string;
  author_name: string;
  body: string;
  created_at: string;
}

export default function PortalCreativePage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [markup, setMarkup] = useState(false);
  // which files the approve covers: 'all' (every file not approved yet) or the ticked ones
  const [confirmApprove, setConfirmApprove] = useState<null | 'all' | 'selected'>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // the note being typed on the open video: Request adjustment lights up as soon as there is
  // one, and saves it first (Aziz 2026-10-26: the button stayed grey while she was writing)
  const [draft, setDraftText] = useState('');
  const [draftFor, setDraftFor] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  /** keep a typed note on the video it was typed on: saved before switching videos */
  async function saveDraft(): Promise<boolean> {
    if (!draft.trim() || !draftFor) return true;
    setSavingDraft(true);
    try {
      await createReview(draftFor, { body: draft.trim() });
      qc.invalidateQueries({ queryKey: ['portal-file-notes', draftFor] });
      setDraftText(''); setDraftFor(null);
      await approval.refetch();
      return true;
    } catch { toast.error('Could not save your note. Try again.'); return false; }
    finally { setSavingDraft(false); }
  }
  const [askAdjust, setAskAdjust] = useState(false);

  const creative = useQuery({
    queryKey: ['portal-creative', id],
    queryFn: async () => (await api.get<CreativeDetail>(`/creatives/${id}`)).data,
    enabled: !!id,
  });
  const assets = useQuery({
    queryKey: ['portal-assets', id],
    queryFn: async () => (await api.get<Asset[]>(`/creatives/${id}/assets`)).data,
    enabled: !!id,
  });
  const comments = useQuery({
    queryKey: ['portal-comments', id],
    queryFn: async () => (await api.get<CommentRow[]>(`/creatives/${id}/comments`)).data,
    enabled: !!id,
  });
  const approval = useQuery({
    queryKey: ['portal-approval', id],
    queryFn: () => fetchPortalApproval(id!),
    enabled: !!id,
  });
  const refreshApproval = () => {
    qc.invalidateQueries({ queryKey: ['portal-approval', id] });
    qc.invalidateQueries({ queryKey: ['portal-creative', id] });
    qc.invalidateQueries({ queryKey: ['portal-creatives'] });
  };
  const postComment = useMutation({
    mutationFn: async (text: string) =>
      (await api.post(`/creatives/${id}/comments`, { body: text })).data,
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: ['portal-comments', id] });
      toast.success('Sent to the team');
    },
  });
  const approve = useMutation({
    mutationFn: (which: 'all' | 'selected') => portalApprove(id!, which === 'all' ? { all: true } : { assetIds: Array.from(selected) }),
    onSuccess: (r) => {
      setConfirmApprove(null); setSelected(new Set());
      toast.success(r.status === 'approved' ? 'Approved. Thank you, the team will take it from here.'
        : `Approved and locked. ${(r.files ?? []).filter((f) => f.approved).length} of ${(r.files ?? []).length} files approved.`);
      refreshApproval();
    },
    onError: () => setConfirmApprove(null),
  });
  const adjust = useMutation({
    mutationFn: (notes: string) => portalRequestAdjustment(id!, notes),
    onSuccess: (a) => {
      setAskAdjust(false);
      toast.success(`Adjustment sent to the team. ${a.adjustments_left} of ${a.adjustments_limit} rounds left.`);
      refreshApproval();
    },
    onError: () => setAskAdjust(false),
  });

  const c = creative.data;
  // Every final file is watchable, not only the newest: MS-4-2026-007 carried 8 final
  // videos and Dr Shaima saw one, the rest only inside a zip (Aziz 2026-10-01).
  const [pick, setPick] = useState<string | null>(null);
  const files = assets.data ?? [];
  const main = files.find((x) => x.id === pick) ?? files[0];
  const [zipping, setZipping] = useState(false);
  const [zipPct, setZipPct] = useState(0);
  const isVideo = (main?.content_type ?? '').startsWith('video');
  const a = approval.data;
  // per file: approved (locked) and the clinic's open notes (client_files.py)
  const state = new Map<string, PortalFileState>((a?.files ?? []).map((f) => [f.asset_id, f]));
  const deciding = a?.status === 'pending' && a.you_are_approver !== false;
  const open = files.filter((f) => !state.get(f.id)?.approved);
  const noted = open.filter((f) => (state.get(f.id)?.notes ?? 0) > 0);
  const ticked = open.filter((f) => selected.has(f.id));
  // Select all picks the files that can be approved: not approved yet and without notes
  const approvable = open.filter((f) => !(state.get(f.id)?.notes));
  const tickedNoted = ticked.filter((f) => (state.get(f.id)?.notes ?? 0) > 0);
  const toggle = (fid: string) => setSelected((x) => { const n = new Set(x); if (n.has(fid)) n.delete(fid); else n.add(fid); return n; });
  const many = files.length > 1;

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-muted-strong">
        <ArrowLeft size={13} /> Back to your work
      </Link>

      {creative.isLoading && <div className="text-sm text-muted py-12 text-center">Loading...</div>}

      {c && (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <div className="text-[11px] font-mono text-muted">{c.code}</div>
              <h1 className="text-lg font-semibold text-muted-strong leading-snug">{c.title}</h1>
              {c.description && <RichView html={c.description_html} plain={c.description} className="text-xs text-muted" testid="portal-brief" />}
            </div>
            {a && <StatusBadge a={a} />}
          </div>

          <div className="bg-bg-card rounded-2xl overflow-hidden border border-bg-card">
            {main?.download_url ? (
              isVideo ? (
                <video
                  controls
                  playsInline
                  poster={main.thumbnail_url ?? undefined}
                  src={main.download_url}
                  className="w-full max-h-[70vh] bg-black"
                />
              ) : (
                <img src={main.download_url} alt={c.title} className="w-full max-h-[70vh] object-contain bg-black" />
              )
            ) : (
              <div className="py-20 text-center text-xs text-muted">
                {assets.isLoading ? 'Loading file...' : 'The final file is not attached yet.'}
              </div>
            )}
            {files.length > 1 && (
              <div className="border-t border-bg px-3 py-2" data-testid="portal-file-strip">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted mb-1.5" data-testid="portal-file-count">
                  <span>{files.length} files on this creative. Tap one to watch it.</span>
                  {a && <span className="text-emerald-400">{files.length - open.length} of {files.length} approved</span>}
                  {deciding && approvable.length > 0 && (
                    <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer text-muted-strong" data-testid="portal-select-all">
                      <input type="checkbox" className="accent-emerald-500" checked={ticked.length === approvable.length && approvable.length > 0}
                        onChange={(e) => setSelected(e.target.checked ? new Set(approvable.map((f) => f.id)) : new Set())} />
                      Select all{ticked.length ? ` (${ticked.length} selected)` : ''}
                    </label>
                  )}
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {files.map((f, i) => {
                    const on = f.id === main?.id;
                    const vid = (f.content_type ?? '').startsWith('video');
                    const st = state.get(f.id);
                    return (
                      <div key={f.id} className={`relative shrink-0 w-28 rounded-lg border ${on ? 'border-accent-400 bg-accent-600/10' : 'border-slate-800 hover:border-slate-600'}`}>
                        <button type="button" onClick={async () => { if (f.id !== main?.id && !(await saveDraft())) return; setPick(f.id); }} data-testid="portal-file" aria-pressed={on} className="w-full text-left p-1.5">
                          <div className="relative w-full aspect-video rounded bg-black overflow-hidden mb-1">
                            {f.thumbnail_url ? <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover" /> : null}
                            <span className="absolute inset-0 flex items-center justify-center text-white/80">{vid ? <Play size={16} /> : <ImageIcon size={16} />}</span>
                            {st?.approved && <span className="absolute bottom-0.5 left-0.5 inline-flex items-center gap-0.5 rounded bg-emerald-600 px-1 text-[9px] font-semibold text-white" data-testid="portal-file-approved"><Lock size={8} /> Approved</span>}
                            {!st?.approved && (st?.notes ?? 0) > 0 && <span className="absolute bottom-0.5 left-0.5 rounded bg-amber-500 px-1 text-[9px] font-semibold text-black" data-testid="portal-file-notes">{st!.notes} note{st!.notes === 1 ? '' : 's'}</span>}
                          </div>
                          <div className="text-[10px] text-muted-strong truncate" title={f.filename}>{i + 1}. <span dir="auto" className={BIDI_CLS}>{bidi(f.filename)}</span></div>
                        </button>
                        {deciding && !st?.approved && !(st?.notes) && (
                          <input type="checkbox" aria-label={`Select ${f.filename}`} checked={selected.has(f.id)} onChange={() => toggle(f.id)} data-testid="portal-file-tick"
                            className="absolute top-2.5 left-2.5 h-4 w-4 accent-emerald-500 cursor-pointer" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ---- decision row ------------------------------------------------- */}
          {a && (
            <section data-testid="signoff" className="bg-bg-card rounded-2xl border border-bg-card p-4 space-y-3">
              {a.status === 'approved' && (
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={22} className="text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-emerald-300">Approved</div>
                    <div className="text-[11px] text-muted">
                      {a.approved_by_name ? `${a.approved_by_name}, ` : ''}{a.approved_at ? new Date(a.approved_at).toLocaleString() : ''}. This is final; the team is preparing it for posting.
                    </div>
                  </div>
                </div>
              )}
              {a.status === 'adjusting' && (
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-amber-400 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-amber-300">Adjustment in progress</div>
                    <div className="text-[11px] text-muted">The team is working on your notes. You will be able to approve or adjust the next cut. {a.adjustments_used} of {a.adjustments_limit} rounds used.</div>
                  </div>
                </div>
              )}
              {a.status === 'pending' && a.you_are_approver === false && (
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-sky-400 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-sky-300">Awaiting approval by {(a.awaiting_names ?? []).join(', ') || 'another contact'}</div>
                    <div className="text-[11px] text-muted">You can watch the file, mark it up and write in the thread. The sign-off was sent to the contact named above.</div>
                  </div>
                  {main?.download_url && (
                    <button onClick={() => setMarkup(true)} className="ml-auto h-9 px-4 rounded-lg border border-line hover:border-accent-400 text-xs font-medium text-muted-strong inline-flex items-center gap-2 transition">
                      <PenLine size={13} /> Mark up the file
                    </button>
                  )}
                </div>
              )}
              {a.status === 'pending' && a.you_are_approver !== false && (
                <>
                  <p className="text-xs text-muted">
                    {many
                      ? <>Tick the files you are happy with and press <b className="text-muted-strong">Approve selected</b>, or <b className="text-muted-strong">Approve all</b>. Approved files are final and locked. Want a change on a video? Open it and write a note saying why (or mark it up), then <b className="text-muted-strong">Request adjustment</b>.</>
                      : <>Happy with it? <b className="text-muted-strong">Approve</b> is final. Not yet? Write a note on the file or mark it up, then <b className="text-muted-strong">Request adjustment</b>.</>}
                    {' '}<span className="text-muted-strong">{a.adjustments_left} of {a.adjustments_limit}</span> adjustment rounds left on this creative.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={async () => { if (await saveDraft()) setAskAdjust(true); }}
                      disabled={!a.can_request || adjust.isPending || savingDraft || (many && noted.length === 0 && !draft.trim())}
                      title={!a.can_request ? `All ${a.adjustments_limit} included adjustment rounds are used. Contact KAF PRO to add more.`
                        : many && noted.length === 0 && !draft.trim() ? 'Write a note on each video you want changed, saying why' : ''}
                      data-testid="portal-request-adjustment"
                      className="h-9 px-4 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-medium inline-flex items-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <RotateCcw size={13} /> Request adjustment
                    </button>
                    {many && (
                      <button
                        onClick={() => setConfirmApprove('selected')}
                        disabled={!a.can_approve || approve.isPending || ticked.length === 0 || tickedNoted.length > 0 || !!draft.trim()}
                        title={draft.trim() ? 'Save your note first' : ticked.length === 0 ? 'Tick the files to approve first' : tickedNoted.length ? 'A ticked file carries your notes: it is waiting for a change' : ''}
                        data-testid="portal-approve-selected"
                        className="h-9 px-4 rounded-lg border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold inline-flex items-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <CheckCircle2 size={13} /> Approve selected{ticked.length ? ` (${ticked.length})` : ''}
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmApprove('all')}
                      disabled={!a.can_approve || approve.isPending || noted.length > 0 || open.length === 0 || !!draft.trim()}
                      title={noted.length ? `${noted.length} file${noted.length === 1 ? '' : 's'} carry your notes. Approve the others with Approve selected, or send the notes with Request adjustment.` : ''}
                      data-testid="portal-approve-all"
                      className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold inline-flex items-center gap-2 transition disabled:bg-slate-700 disabled:text-white/50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle2 size={13} /> {many ? `Approve all${open.length ? ` (${open.length})` : ''}` : 'Approve'}
                    </button>
                  </div>
                  {noted.length > 0 && (
                    <p className="text-[11px] text-amber-400" data-testid="portal-noted">
                      {noted.length} file{noted.length === 1 ? '' : 's'} with your notes {noted.length === 1 ? 'is' : 'are'} waiting for a change, so {noted.length === 1 ? 'it' : 'they'} cannot be approved. Send the notes with Request adjustment, or remove a note to approve that file.
                    </p>
                  )}
                  {!a.can_request && (
                    <p className="text-[11px] text-amber-400">All {a.adjustments_limit} included adjustment rounds have been used on this creative. Contact KAF PRO if you need more.</p>
                  )}
                </>
              )}
            </section>
          )}

          {main && a && <FileNotes key={main.id} assetId={main.id} filename={main.filename} state={state.get(main.id)} text={draftFor === main.id ? draft : ''} setText={(v) => { setDraftText(v); setDraftFor(main.id); }}
            canWrite={a.status === 'pending'} onChange={refreshApproval} onMarkup={main.download_url ? () => setMarkup(true) : undefined} />}

          <div className="flex items-center gap-3 flex-wrap">
            {main?.download_url && (
              <a
                href={main.attachment_url ?? main.download_url}
                download={main.filename}
                className="h-9 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium inline-flex items-center gap-2 transition"
              >
                <Download size={13} /> Download
              </a>
            )}
            {(assets.data?.length ?? 0) > 1 && (
              <button
                onClick={async () => {
                  setZipping(true);
                  try { await downloadAssetsZip(c.id, [], (j) => setZipPct(j.pct)); }
                  catch { toast.error('Could not build the zip. Try again in a moment.'); }
                  finally { setZipping(false); }
                }}
                disabled={zipping}
                className="h-9 px-4 rounded-lg border border-line hover:border-accent-400 text-xs font-medium text-muted-strong inline-flex items-center gap-2 transition disabled:opacity-50"
              >
                <Download size={13} /> {zipping ? `Preparing zip ${zipPct}%` : `Download all ${assets.data?.length} files`}
              </button>
            )}
            {/* every place it went live (migration 076); older pieces carry one link */}
            {(c.posted_links?.length ? c.posted_links : c.posted_url ? [{ platform: '', url: c.posted_url }] : []).map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="text-xs text-accent-400 hover:underline" data-testid="portal-posted-link">
                {l.platform ? `View on ${postPlaceLabel(l.platform)}` : 'View the published post'}
              </a>
            ))}
            {files.length > 1 && main && (
              <span className="text-[11px] text-muted" data-testid="portal-file-which">File {files.findIndex((x) => x.id === main.id) + 1} of {files.length}</span>
            )}
          </div>

          <section className="space-y-3 pt-2">
            <h2 className="text-sm font-semibold text-muted-strong inline-flex items-center gap-2">
              <MessageSquare size={14} className="text-accent-400" /> Feedback thread
            </h2>
            <div className="space-y-2.5">
              {(comments.data ?? []).map((m) => (
                <div key={m.id} className="bg-bg-card rounded-xl px-3.5 py-2.5">
                  <div className="text-[10px] text-muted mb-0.5">
                    {m.author_name} &middot; {new Date(m.created_at).toLocaleString()}
                  </div>
                  {m.body !== '(file)' && <div className="text-xs text-muted-strong whitespace-pre-wrap leading-relaxed">{m.body}</div>}
                  {!!m.attachments?.length && <Attachments items={m.attachments} />}
                </div>
              ))}
              {comments.data && comments.data.length === 0 && (
                <p className="text-[11px] text-muted">No messages yet. Anything you write here goes straight to the team.</p>
              )}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (body.trim()) postComment.mutate(body.trim());
              }}
              className="flex gap-2"
            >
              <input
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write feedback for the team..."
                className="flex-1 h-10 px-3 rounded-lg border border-line bg-bg text-xs text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400"
              />
              <button
                type="submit"
                disabled={postComment.isPending || !body.trim()}
                className="h-10 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium disabled:opacity-50 transition"
              >
                Send
              </button>
            </form>
          </section>

          {/* The same review tool the team uses: frame-accurate notes with lines,
              arrows, boxes and colours. Markers are filed against the file and the
              team sees them on the creative. */}
          {markup && main && (
            <ReviewModal
              asset={main as unknown as MediaAsset}
              onClose={() => { setMarkup(false); refreshApproval(); }}
            />
          )}
          <ConfirmDialog
            open={confirmApprove !== null}
            title={!many ? 'Approve this creative?' : confirmApprove === 'all' ? `Approve all ${open.length} files?` : `Approve ${ticked.length} file${ticked.length === 1 ? '' : 's'}?`}
            message={<span>This is <b>final and cannot be undone</b>. {many
              ? (confirmApprove === 'all' || ticked.length === open.length
                ? 'Every file is then approved and locked, and the team proceeds to posting.'
                : 'These files are locked and will not be asked again. The rest stay with you to approve or adjust.')
              : 'Once approved, no further adjustments can be requested and the team proceeds to posting.'}</span>}
            confirmLabel="Yes, approve"
            cancelLabel="Not yet"
            tone="info"
            onConfirm={() => { if (confirmApprove) approve.mutate(confirmApprove); }}
            onCancel={() => setConfirmApprove(null)}
          />
          <PromptDialog
            open={askAdjust}
            title="Request an adjustment"
            message={<span>{noted.length
              ? <>Your notes on {noted.length} file{noted.length === 1 ? '' : 's'} go to the team. Add anything else here (optional).</>
              : <>Describe exactly what should change. Tip: write a note on the file or use <b>Mark up the file</b> to point at the spot.</>}
              {' '}This uses 1 of your {a?.adjustments_left ?? 0} remaining round{(a?.adjustments_left ?? 0) === 1 ? '' : 's'}.</span>}
            placeholder="What should be different?"
            confirmLabel="Send adjustment"
            allowEmpty={noted.length > 0}
            multiline
            maxLength={4000}
            onConfirm={(v) => { if (v.trim() || noted.length) adjust.mutate(v.trim()); else toast.error('Please describe the adjustment, or write a note on the file'); }}
            onCancel={() => setAskAdjust(false)}
          />
        </>
      )}
    </div>
  );
}

function StatusBadge({ a }: { a: PortalApproval }) {
  if (a.status === 'approved') {
    return (
      <span data-testid="status-approved" className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <CheckCircle2 size={12} /> Approved
      </span>
    );
  }
  if (a.status === 'adjusting') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Clock size={12} /> Adjusting
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">
      {a.you_are_approver === false ? 'In review' : 'Awaiting your approval'}
    </span>
  );
}

/** The clinic's notes on one file (asset reviews: the team sees them on the file). A file
 *  with notes is waiting for a change; removing the note makes it approvable again. */
function FileNotes({ assetId, filename, state, canWrite, onChange, onMarkup, text, setText }: {
  assetId: string; filename: string; state?: PortalFileState; canWrite: boolean; onChange: () => void; onMarkup?: () => void;
  text: string; setText: (v: string) => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const notes = useQuery({ queryKey: ['portal-file-notes', assetId], queryFn: () => fetchAssetReviews(assetId) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['portal-file-notes', assetId] }); onChange(); };
  const add = useMutation({
    mutationFn: (body: string) => createReview(assetId, { body }),
    onSuccess: () => { setText(''); toast.success('Note saved on this file'); refresh(); },
  });
  const remove = useMutation({
    mutationFn: async (rid: string) => (await api.delete(`/reviews/${rid}`)).data,
    onSuccess: () => { toast.success('Note removed'); refresh(); },
  });
  const rows = (notes.data ?? []).filter((r) => !r.parent_id);
  const locked = !!state?.approved;
  return (
    <section className="bg-bg-card rounded-2xl border border-bg-card p-4 space-y-2.5" data-testid="portal-file-notes-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-muted-strong truncate">Notes on <span dir="auto" className={BIDI_CLS}>{bidi(filename)}</span></h2>
        {locked
          ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><Lock size={11} /> Approved{state?.approved_by_name ? ` by ${state.approved_by_name}` : ''}, locked</span>
          : onMarkup && canWrite && <button onClick={onMarkup} className="h-8 px-3 rounded-lg border border-line hover:border-accent-400 text-[11px] font-medium text-muted-strong inline-flex items-center gap-1.5"><PenLine size={12} /> Mark up this file</button>}
      </div>
      {rows.length === 0 && <p className="text-[11px] text-muted">No notes on this file.</p>}
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg bg-bg px-3 py-2 text-xs">
          <div className="flex items-center justify-between gap-2 text-[10px] text-muted">
            <span>{r.author_name} · {new Date(r.created_at).toLocaleString()}{r.timestamp_sec != null ? ` · at ${Number(r.timestamp_sec).toFixed(1)}s` : ''}{r.drawing?.length ? ' · marked up' : ''}{r.status === 'resolved' ? ' · done' : ''}</span>
            {!locked && canWrite && r.author_id === user?.id && r.status === 'open' && (
              <button onClick={() => remove.mutate(r.id)} aria-label="Remove this note" className="text-muted hover:text-red-400"><Trash2 size={12} /></button>
            )}
          </div>
          <div className="text-muted-strong whitespace-pre-wrap mt-0.5" dir="auto">{r.body}</div>
        </div>
      ))}
      {!locked && canWrite && (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) add.mutate(text.trim()); }} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} dir="auto" data-testid="portal-file-note-input"
            placeholder="What should change on this file?"
            className="flex-1 h-9 px-3 rounded-lg border border-line bg-bg text-xs text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400" />
          <button type="submit" disabled={add.isPending || !text.trim()} className="h-9 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium disabled:opacity-50">Save note</button>
        </form>
      )}
    </section>
  );
}
